import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import React from "react";
import "../../../css/Adviser/Pages/AdviserJoinUsPage.css";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import CardProduct2 from "frontend/Components/DesignSystem/Cards/CardProduct2";
import CardProduct2PopUp from "frontend/Components/DesignSystem/Cards/CardProduct2PopUp";
import { useState } from "react";
import ButtonGreen from "frontend/Components/DesignSystem/Buttons/ButtonGreen";
import { RequestBrochureDetails } from "frontend/Components/RequestBrochure/requestBrochurePopUp";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * JoinUsPage component displays the "Benefits of joining Rosemount" page for advisers.
 * 
 * Features:
 * - Renders navigation bar, hero section, and multiple benefit sections using CardProduct2 components.
 * - Allows users to view detailed information about each benefit in a popup modal.
 * - Provides an option to request a brochure via a modal form.
 * - Displays options for going self-employed as a Registered Individual (RI) or Appointed Representative (AR).
 * - Includes contact and footer sections tailored for advisers.
 * 
 * State:
 * - `popupVisible` (boolean): Controls visibility of the benefit details popup.
 * - `popupContent` (object): Holds the title and content for the popup modal.
 * - `isRequestBrochure` (boolean): Controls visibility of the brochure request modal.
 * 
 * Methods:
 * - `handleCardClick(title, content)`: Opens the popup modal with the given title and content.
 * - `closePopup()`: Closes the popup modal and resets its content.
 * - `handleCloseRequestBrochure()`: Closes the brochure request modal.
 * 
 * @component
 * @returns {JSX.Element} The rendered JoinUsPage component.
 */
const JoinUsPage = () => {
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupContent, setPopupContent] = useState({ title: "", content: "" });
  const [isRequestBrochure, setRequestBrochure] = useState(false);
  const navigate = useNavigate();

  const navigationItems = [
    { label: "Home", navigateTo: "/Adviser" },
    { label: "Join Us", navigateTo: "/Adviser/JoinUs" },
    { label: "About Us", navigateTo: "/Adviser/AboutUs" },
    {
      label: "Contact Us",
      navigateTo: "/ContactUs",
      state: { isAdviser: true },
    },
  ];

  /**
   * Handles the click event on a card by setting the popup content and making the popup visible.
   *
   * @param {string} title - The title to display in the popup.
   * @param {string} content - The content to display in the popup.
   */
  const handleCardClick = (title, content) => {
    setPopupContent({ title, content });
    setPopupVisible(true);
  };

  /**
   * Closes the popup by setting its visibility to false and clearing its content.
   *
   * This function updates the popup state by:
   * - Hiding the popup.
   * - Resetting the popup content to default empty values.
   */
  const closePopup = () => {
    setPopupVisible(false);
    setPopupContent({ title: "", content: "" });
  };

  /**
   * Handles the closing of the "Request Brochure" modal or dialog.
   * Sets the state to hide the request brochure component.
   *
   * @function
   * @returns {void}
   */
   const handleCloseRequestBrochure = () => {
     setRequestBrochure(false);
   };


  return (
    <div className="JoinUs-Page">
      <SeoHelmet
        page={SEO_PAGES.joinUs}
        service={{
          name: "Adviser Network Support",
          description:
            "Rosemount provides adviser network support including compliance, paraplanning, marketing, technology, training and business development support.",
          serviceType: [
            "Mortgage Adviser Support",
            "Protection Adviser Support",
            "Financial Planning Adviser Support",
            "Compliance Support",
            "Marketing Support",
            "Paraplanning Support",
            "Business Development Support",
            "Technology Support",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Advisers",
            url: getPageUrl("/Adviser"),
          },
          {
            name: "Join Us",
            url: getPageUrl("/Adviser/JoinUs"),
          },
        ]}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["join-us"]}
        isAdviser={true}
        altText="Two hands shaking with a watch on display."
        children={
          <>
            <h1>Benefits of joining Rosemount</h1>
            <div className="ButtonGreen">
              <ButtonGreen
                buttonText="Request our Brochure"
                // onClick={requestBrochure}
                onClick={() => setRequestBrochure(true)}
              />
            </div>
          </>
        }
      />

      <div className="JoinUs-Container">
        <h2>Support for your business</h2>

        {/* <div
          className="ViewBrochure-Link"
          onClick={() => navigate("/Adviser/Brochure")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && navigate("/Adviser/Brochure")}
        >
          View Brochure
        </div> */}

        {/* <a
            href="/documents/Rosemount%20Financial%20Solutions%20(IFA)%20brochure.pdf"
            className="_df_button"
            data-dflip="true"
            target="_blank"
            rel="noreferrer"
            style={{color: "black"}}
          >
            Open Brochure
          </a> */}

        {/* <div
          // className="_df_button"
          // className="_df_book"
          className="_df_thumb"
          data-source="http://192.168.88.129:5000/documents/Rosemount%20Financial%20Solutions%20(IFA)%20brochure.pdf"
          data-tags="3d,images"
          thumb="http://192.168.88.129:5000/documents/Thumbnail.png"
        >
          View Brochure
        </div> */}

        <div className="JoinUs-Cards-Container">
          <CardProduct2
            title="Marketing Support"
            isAdviser={true}
            onClick={() =>
              handleCardClick(
                "Marketing Support",
                "We offer an in-house service that can provide you with marketing material and social media support. This will reduce your costs and be a useful tool when generating new business. This service is included in your fees and the service is available five days a week.",
              )
            }
          />
          <CardProduct2
            title="Systems"
            isAdviser={true}
            onClick={() =>
              handleCardClick(
                "Systems",
                "We use state of the art systems to help you keep your business efficient and running smoothly. From our CRM to our ID Verification, we are always ensuring we are ahead of the curve on technology.",
              )
            }
          />
          <CardProduct2
            title="Individual Tailored Contracts"
            isAdviser={true}
            onClick={() =>
              handleCardClick(
                "Individual Tailored Contracts",
                "Unlike other companies, we tailor each contract to your personal needs. We take on board your feedback and adapt to make the contract beneficial to both parties. We want to work with you to get the best possible results.",
              )
            }
          />
          <CardProduct2
            title="Recruitment Support"
            isAdviser={true}
            onClick={() =>
              handleCardClick(
                "Recruitment Support",
                "As part of the network we provide support for all areas of your business. When your business grows, you may need to recruit staff. This can be time consuming and take you away from your role. Whether you are looking to take on an administrator or a fully qualified adviser, we can help create job adverts, conduct interviews and provide support with HR and contracts.",
              )
            }
          />
          <CardProduct2
            title="Business Development Support"
            isAdviser={true}
            onClick={() =>
              handleCardClick(
                "Business Development Support",
                "When your business grows, so does your need for business support. This can be in marketing, business generation, or a host of other activities across the business. Our BDMs work with you to help you reach your potential and achieve your goals. Our BDMs will also provide invaluable support to increase your introducer network, lead generation, and yield per meeting. We ensure that you are provided the platform and support to grow your client network as far as you can.",
              )
            }
          />
          <CardProduct2
            title="Succession Planning and Acquisitions"
            isAdviser={true}
            onClick={() =>
              handleCardClick(
                "Succession Planning and Acquisitions",
                "We offer succession planning for your business, and will discuss a bespoke offer suitable for you, when you decide the time is right.",
              )
            }
          />
        </div>
        {popupVisible && (
          <div className="Popup-Overlay" onClick={closePopup}>
            <div
              className="Card-Product2-PopUp"
              onClick={(e) => e.stopPropagation()} // Prevent closing popup on content click
            >
              <CardProduct2PopUp
                title={popupContent.title}
                content={popupContent.content}
                onClick={closePopup}
              />
            </div>
          </div>
        )}
      </div>
      <div className="JoinUs-Container">
        <h2>Working with an Independent Network</h2>

        <div className="JoinUs-Cards-Container">
          <CardProduct2
            title="One Stop Support"
            isAdviser={true}
            onClick={() => {
              handleCardClick(
                "One Stop Support",
                "One of the primary benefits of joining Rosemount is the overall support and speed of service. We do not operate phone menus; you speak to a real human each time! We benefit from every department working within the same building, allowing seamless collaboration between each of our teams.",
              );
            }}
          />
          <CardProduct2
            title="Fund Matrix"
            isAdviser={true}
            onClick={() => {
              handleCardClick(
                "Fund Matrix",
                "Our completely independent fund matrix includes some of the most highly rated funds available in the UK. All funds have high ratings from a combination of Defaqto, Morningstar, FE Trustnet and other ratings agencies. Then we only select those with costs at the lower end of the spectrum. Ensuring that the funds have the very best prospects for your customers. A choice of passive, actively managed or ESG funds are available for each risk profile. We continuously refine our Fund Matrix through adviser feedback, enabling us to adapt to changing market dynamics.",
              );
            }}
          />
          <CardProduct2
            title="Independence"
            isAdviser={true}
            onClick={() => {
              handleCardClick(
                "Independence",
                "At Rosemount, we understand the importance of genuine independence for financial advisers. Unlike many firms bound by affiliations with parent life insurance companies or fund managers, we offer complete autonomy. Our advisers are truly independent, granting them the freedom to provide clients with unbiased advice and create customised solutions that sets them apart from the competition.",
              );
            }}
          />
          <CardProduct2
            title="Training"
            isAdviser={true}
            onClick={() => {
              handleCardClick(
                "Training",
                "Rosemount offer bespoke training for our advisers. Whether you are wishing to specialise in different areas or build up your knowledge on something new, we can arrange a session tailored to your individual needs. We also run regional ‘Peer Groups’ throughout the year to help build and maintain knowledge in line with CPD requirements.",
              );
            }}
          />
        </div>
      </div>
      <div className="JoinUs-Container">
        <h2>Empowering your advice</h2>
        <div className="JoinUs-Cards-Container">
          <CardProduct2
            title="Paraplanning Service"
            isAdviser={true}
            onClick={() => {
              handleCardClick(
                "Paraplanning Service",
                "Unlike many other networks within the industry, we offer a dedicated paraplanning support service for our advisers. Our team are on hand to provide administrative assistance for you and your team, improving efficiency and allowing you to focus on what you are good at… advising! Our paraplanners can offer support across all business areas: Mortgage, Equity Release, Protection, and Financial Planning.",
              );
            }}
          />
          <CardProduct2
            title="Incentive Schemes"
            isAdviser={true}
            onClick={() => {
              handleCardClick(
                "Incentive Schemes",
                "We run many exciting incentive schemes for our advisers, from awards for our most compliant advisers right through to our biggest producers. In addition, we run an annual convention for our top advisers. Conventions in the past have taken place in thrilling and unique destinations including but not limited to Las Vegas, Singapore, The Maldives, and Bali.",
              );
            }}
          />
          <CardProduct2
            title="Compliance Support"
            isAdviser={true}
            onClick={() => {
              handleCardClick(
                "Compliance Support",
                "We provide support with understanding FCA regulation and rules through our clear and concise advice standards. Our dedicated compliance support team is available to answer questions you may have, ensuring that you can continue to transact business in a sensible and timely manner. Adviser compliance training spans vital areas such as regulatory adherence, ethics, client confidentiality, anti-money laundering, market conduct, risk assessment, and industry regulations. It underscores clear communication, record-keeping, and keeping up with evolving",
              );
            }}
          />
        </div>
      </div>
      <div className="JoinUs-Option-Container">
        <div className="Join-Us-Content">
          <div className="JoinUs-Container">
            <h2>How you can work with Rosemount</h2>
            <div className="JoinUs-Cards-Container">
              <CardProduct2
                title="Registered Individual (RI)"
                isAdviser={true}
                onClick={() => {
                  handleCardClick(
                    "Registered Individual (RI)",
                    "If you were to join as an RI you would be associated with an established authorised firm, which can enhance your credibility and reputation in the industry boosting trust and confidence in your services. If you were to join as an RI you would be associated with an established authorised firm, which can enhance your credibility and reputation in the industry boosting trust and confidence in your services.",
                  );
                }}
              />
              <CardProduct2
                title="Appointed Representative (AR)"
                isAdviser={true}
                onClick={() => {
                  handleCardClick(
                    "Appointed Representative (AR)",
                    "With this option, you will be able to trade under your own AR with your own identity and FCA number. As an AR, Rosemount are responsible for your compliance whilst also monitoring your quality and competency. This route can help you build up your own business and branding from scratch with full and comprehensive business support from Rosemount. You can maintain your own client relationships and have more autonomy in decision-making, within the boundaries set by Rosemount.",
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <ContactUs isAdviser={true} />
      <Footer isAdviser={true} />
      {isRequestBrochure && (
        <div className="notification-overlay">
          <RequestBrochureDetails onClose={handleCloseRequestBrochure} />
        </div>
      )}
    </div>
  );
};

export default JoinUsPage;
